import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Tag, CheckCircle } from "lucide-react";

export default function Checkout() {
  const { user } = useAuth();
  const { refresh } = useCart();
  const nav = useNavigate();
  const [totals, setTotals] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [payment, setPayment] = useState("razorpay");
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [placing, setPlacing] = useState(false);

  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);

  const loadTotals = async (code = null) => {
    const r = await api.post("/checkout/summary", { coupon_code: code });
    setTotals(r.data);
  };
  useEffect(() => { loadTotals(); }, []);

  const applyCoupon = async () => {
    try {
      await api.get(`/coupons/validate/${coupon}`);
      await loadTotals(coupon);
      setApplied(coupon);
      toast.success("Coupon applied");
    } catch { toast.error("Invalid coupon code"); }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const r = await api.post("/checkout/create-order", { address: addr, coupon_code: applied, payment_method: payment });
      const { order_id, razorpay_order_id, razorpay_key_id, amount } = r.data;

      if (payment === "cod") {
        toast.success("Order placed! Pay on delivery.");
        refresh();
        nav(`/account?tab=orders&order=${order_id}`);
        return;
      }

      if (!razorpay_key_id) {
        // Test mode - Razorpay keys not configured: verify with dummy data
        toast.info("Test mode: simulating payment");
        await api.post("/checkout/verify", { order_id, razorpay_order_id: "test", razorpay_payment_id: "test_pay", razorpay_signature: "test" });
        toast.success("Payment successful");
        refresh();
        nav(`/account?tab=orders&order=${order_id}`);
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Failed to load payment gateway"); return; }
      const rp = new window.Razorpay({
        key: razorpay_key_id,
        amount,
        currency: "INR",
        name: "SSB Copier",
        description: "Order Payment",
        order_id: razorpay_order_id,
        prefill: { name: user.name, email: user.email, contact: addr.phone },
        theme: { color: "#1E3A8A" },
        handler: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful");
            refresh();
            nav(`/account?tab=orders&order=${order_id}`);
          } catch { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => toast.error("Payment cancelled") },
      });
      rp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    } finally { setPlacing(false); }
  };

  if (!totals) return <div className="p-10">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-950 mb-8">Secure Checkout</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-8">
          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-4">Shipping Address</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={addr.full_name} onChange={(e) => setAddr({...addr, full_name: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-name" />
              <input required placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({...addr, phone: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-phone" />
              <input required placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr({...addr, line1: e.target.value})} className="sm:col-span-2 border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-line1" />
              <input placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({...addr, line2: e.target.value})} className="sm:col-span-2 border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-line2" />
              <input required placeholder="City" value={addr.city} onChange={(e) => setAddr({...addr, city: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-city" />
              <input required placeholder="State" value={addr.state} onChange={(e) => setAddr({...addr, state: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-state" />
              <input required placeholder="Pincode" value={addr.pincode} onChange={(e) => setAddr({...addr, pincode: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="checkout-pincode" />
            </div>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-4">Payment Method</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[{k:"razorpay",l:"Online Payment (Razorpay)"},{k:"cod",l:"Cash on Delivery"}].map((m) => (
                <button type="button" key={m.k} onClick={() => setPayment(m.k)} className={`p-4 rounded-xl border text-left transition ${payment === m.k ? "border-blue-900 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`payment-${m.k}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-900">{m.l}</div>
                    {payment === m.k && <CheckCircle className="w-5 h-5 text-blue-900" />}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {m.k === "razorpay" ? "UPI, Cards, Netbanking, Wallets" : "Pay when the order arrives"}
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>
        <aside className="bg-slate-50 border border-slate-200 rounded-2xl p-6 h-fit lg:sticky lg:top-24">
          <div className="font-heading text-lg font-semibold mb-4">Order Summary</div>
          <div className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-2">
            {totals.items.map((it) => (
              <div key={it.product_id} className="flex gap-3 text-sm">
                <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-white" />
                <div className="flex-1"><div className="line-clamp-1 font-medium">{it.name}</div><div className="text-xs text-slate-500">Qty {it.quantity}</div></div>
                <div>₹{(it.price * it.quantity).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-4 mb-4">
            <div className="flex gap-2 mb-3">
              <div className="flex-1 relative">
                <Tag className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="w-full border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-sm bg-white" data-testid="checkout-coupon-input" />
              </div>
              <button type="button" onClick={applyCoupon} className="bg-blue-900 text-white px-4 py-2.5 rounded-xl text-sm" data-testid="checkout-coupon-apply">Apply</button>
            </div>
            <div className="text-xs text-slate-500">Try SSB10, FLAT500, WELCOME</div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>₹{totals.subtotal.toLocaleString("en-IN")}</span></div>
            {totals.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−₹{totals.discount.toLocaleString("en-IN")}</span></div>}
            <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span>{totals.shipping ? `₹${totals.shipping}` : "Free"}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">GST (18%)</span><span>₹{totals.tax.toLocaleString("en-IN")}</span></div>
          </div>
          <div className="border-t border-slate-200 my-4"></div>
          <div className="flex justify-between font-heading font-bold text-lg mb-6"><span>Total</span><span>₹{totals.total.toLocaleString("en-IN")}</span></div>
          <button disabled={placing} className="w-full bg-blue-900 hover:bg-blue-800 disabled:opacity-60 text-white py-3.5 rounded-xl font-medium" data-testid="checkout-place-order">
            {placing ? "Processing..." : "Place Order"}
          </button>
        </aside>
      </form>
    </div>
  );
}
